import React, { useState, useMemo } from "react";
import { observer } from "mobx-react";
import { useNextStepsDebug } from "@/hooks/useNextStepsDebug";
import { useNextSteps } from "@/hooks/useNextSteps";
import { useUpdateNextStepStatus } from "@/hooks/useUpdateNextStepStatus";

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Pagination,
  Tabs,
  Tab,
  ListItemIcon,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Iconify } from "@/components/iconify/iconify";
import { CriteriaResultItem } from "@/components/debug/CriteriaResultItem";

const NextStepCard = ({ nextStep }) => {
  return (
    <Card
      sx={{
        height: "100%",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom color="text.primary">
          {nextStep.title} (ID: {nextStep.id})
        </Typography>
        <Typography variant="body2" gutterBottom color="text.secondary">
          Order: {nextStep.order}
        </Typography>
        <Typography variant="body2" gutterBottom color="text.secondary">
          Status: {nextStep.status}
        </Typography>
        {nextStep.progress && (
          <Typography variant="body2" gutterBottom color="text.secondary">
            Progress: {nextStep.progress.current}/{nextStep.progress.target}
          </Typography>
        )}
        <Typography variant="body2" gutterBottom color="text.secondary">
          Description: {nextStep.description || "No description"}
        </Typography>
        <Chip
          label={`Go to ${nextStep.url}`}
          component={Link}
          to={nextStep.url}
          clickable
          variant="outlined"
          sx={{
            mt: 1,
            "&:hover": { bgcolor: "action.hover" },
          }}
        />
      </CardContent>
    </Card>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case "CURRENTLY_ACTIVE":
      return { color: "#4caf50", icon: "eva:play-circle-fill" };
    case "AVAILABLE_FIRST_IN_STRAND":
      return { color: "#2196f3", icon: "eva:checkmark-circle-2-fill" };
    case "AVAILABLE_NOT_FIRST_IN_STRAND":
      return { color: "#ff9800", icon: "eva:clock-fill" };
    case "NOT_AVAILABLE":
      return { color: "#f44336", icon: "eva:close-circle-fill" };
    case "PREVIOUSLY_COMPLETED":
      return { color: "#9c27b0", icon: "eva:checkmark-circle-fill" };
    case "COMPLETED_AVAILABLE":
      return { color: "#00bcd4", icon: "eva:award-fill" };
    case "COMPLETED_NOT_AVAILABLE":
      return { color: "#607d8b", icon: "eva:slash-fill" };
    default:
      return { color: "#757575", icon: "eva:question-mark-circle-fill" };
  }
};

const CheckResultDisplay = ({ checkData, title, onEventGenerated }) => {
  if (!checkData) return null;

  const data =
    typeof checkData === "string" ? JSON.parse(checkData) : checkData;
  const results = data.results || data.debug?.results || [];
  const satisfied =
    data.satisfied !== undefined ? data.satisfied : data.debug?.satisfied;
  const totalCriteria =
    data.totalCriteria || data.debug?.totalCriteria || results.length;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="primary.main" gutterBottom>
        {title}:
      </Typography>
      <Box
        sx={{
          pl: 2,
          borderLeft: 2,
          borderColor: satisfied ? "success.main" : "error.main",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Chip
            label={satisfied ? "Satisfied" : "Not Satisfied"}
            color={satisfied ? "success" : "error"}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            {totalCriteria} criteria total
          </Typography>
        </Box>

        {results.map((result, index) => (
          <CriteriaResultItem
            key={result.criterionId || index}
            result={result}
            index={index}
            onEventGenerated={onEventGenerated}
          />
        ))}
      </Box>
    </Box>
  );
};

const DebugStepItem = ({
  debugStep,
  showDetails = false,
  onEventGenerated,
}) => {
  const [expanded, setExpanded] = useState(false);
  const statusStyle = getStatusColor(debugStep.status);

  const handleToggleDetails = () => {
    setExpanded(!expanded);
  };

  return (
    <ListItem
      sx={{
        bgcolor: "background.paper",
        mb: 1,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        flexDirection: "column",
        alignItems: "stretch",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <ListItemIcon sx={{ minWidth: 40 }}>
          <Iconify
            icon={statusStyle.icon}
            sx={{ color: statusStyle.color, fontSize: 24 }}
            label="Status icon"
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" color="text.primary">
                {debugStep.title}
              </Typography>
              <Chip
                label={debugStep.status.replace("_", " ")}
                size="small"
                sx={{
                  bgcolor: statusStyle.color + "20",
                  color: statusStyle.color,
                  fontWeight: "bold",
                }}
              />
              <Typography variant="body2" color="text.secondary">
                (ID: {debugStep.id}, Order: {debugStep.order})
              </Typography>
            </Box>
          }
          secondary={
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Strand: <b>{debugStep.strand || "None"}</b> |{" "}
                {debugStep.description || "No description"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontStyle: "italic", color: "text.secondary", mt: 0.5 }}
              >
                {debugStep.statusReason}
              </Typography>
              {debugStep.progress && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Progress: {debugStep.progress.current}/
                  {debugStep.progress.target}
                </Typography>
              )}
            </Box>
          }
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {debugStep.url && (
            <Chip
              label="Visit"
              component={Link}
              to={debugStep.url}
              clickable
              size="small"
              variant="outlined"
            />
          )}
          {showDetails &&
            (debugStep.availabilityCheck || debugStep.completionCheck) && (
              <Button
                size="small"
                onClick={handleToggleDetails}
                startIcon={
                  <Iconify
                    icon={
                      expanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"
                    }
                  />
                }
                label="Toggle details"
              >
                Details
              </Button>
            )}
        </Box>
      </Box>

      {/* Always show availability criteria for completed climbs */}
      {debugStep.availabilityCheck && (
        <Box sx={{ mt: 2, pl: 6, borderTop: 1, borderColor: "divider", pt: 2 }}>
          <CheckResultDisplay
            checkData={debugStep.availabilityCheck}
            title="Availability Check"
            onEventGenerated={onEventGenerated}
          />
        </Box>
      )}

      {/* Show completion check details when expanded */}
      {expanded && showDetails && debugStep.completionCheck && (
        <Box sx={{ mt: 2, pl: 6, borderTop: 1, borderColor: "divider", pt: 2 }}>
          <CheckResultDisplay
            checkData={debugStep.completionCheck}
            title="Completion Check"
            onEventGenerated={onEventGenerated}
          />
        </Box>
      )}
    </ListItem>
  );
};

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`debug-tabpanel-${index}`}
      aria-labelledby={`debug-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

export const NextStepsDebug = observer(() => {
  const [tabValue, setTabValue] = useState(0);
  const [userEventsPage, setUserEventsPage] = useState(1);
  const eventsPerPage = 50;

  // Status filter state - all statuses enabled by default
  const [statusFilters, setStatusFilters] = useState({
    CURRENTLY_ACTIVE: true,
    AVAILABLE_FIRST_IN_STRAND: true,
    AVAILABLE_NOT_FIRST_IN_STRAND: true,
    NOT_AVAILABLE: true,
    PREVIOUSLY_COMPLETED: true,
    COMPLETED_AVAILABLE: true,
    COMPLETED_NOT_AVAILABLE: true,
  });

  // Strand filter state - all strands enabled by default
  const [strandFilters, setStrandFilters] = useState({});

  // Text filter state
  const [textFilter, setTextFilter] = useState("");

  const {
    nextStepsDebugData,
    loading: debugLoading,
    error: debugError,
    refetch: refetchDebug,
  } = useNextStepsDebug();
  const {
    nextSteps: actualNextSteps,
    loading: nextStepsLoading,
    error: nextStepsError,
    refetch: refetchNextSteps,
  } = useNextSteps();
  const { updateNextStepStatusAsync, loading: updatingStatus } =
    useUpdateNextStepStatus();

  const handleRefetch = async () => {
    await Promise.all([refetchDebug(), refetchNextSteps()]);
  };

  const handleDismissStep = async (stepId) => {
    try {
      await updateNextStepStatusAsync({
        nextStepId: stepId,
        status: "completed",
      });
      // Refetch data after successful update
      await handleRefetch();
    } catch (error) {
      console.error("Failed to dismiss step:", error);
    }
  };

  const handleEventGenerated = async (eventType) => {
    console.log(`Event generated: ${eventType}`);
    // Refetch data after generating an event to see updated criteria
    await handleRefetch();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilters((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const handleStrandFilterChange = (strand) => {
    setStrandFilters((prev) => ({
      ...prev,
      [strand]: !prev[strand],
    }));
  };

  const handleTextFilterChange = (event) => {
    setTextFilter(event.target.value);
  };

  const {
    userEventsCount,
    availableStepsCount,
    existingIncompleteStepsCount,
    userEvents,
    debugSteps,
  } = nextStepsDebugData || {};

  // Sort and paginate user events
  const sortedAndPaginatedUserEvents = useMemo(() => {
    if (!userEvents) return { events: [], totalPages: 0 };

    // Sort by timestamp descending (newest first)
    const sorted = [...userEvents].sort(
      (a, b) => parseInt(b.timestamp) - parseInt(a.timestamp),
    );

    // Calculate pagination
    const totalPages = Math.ceil(sorted.length / eventsPerPage);
    const startIndex = (userEventsPage - 1) * eventsPerPage;
    const endIndex = startIndex + eventsPerPage;
    const paginatedEvents = sorted.slice(startIndex, endIndex);

    return { events: paginatedEvents, totalPages };
  }, [userEvents, userEventsPage, eventsPerPage]);

  // Extract unique strands and initialize strand filters
  const availableStrands = useMemo(() => {
    if (!debugSteps) return [];

    const strands = [
      ...new Set(debugSteps.map((step) => step.strand).filter(Boolean)),
    ];
    return strands.sort();
  }, [debugSteps]);

  // Initialize strand filters when available strands change
  React.useEffect(() => {
    if (availableStrands.length > 0) {
      setStrandFilters((prev) => {
        const newFilters = { ...prev };
        availableStrands.forEach((strand) => {
          if (!(strand in newFilters)) {
            newFilters[strand] = true; // Enable all strands by default
          }
        });
        return newFilters;
      });
    }
  }, [availableStrands]);

  // Sort and filter debug steps by order, status, and strand
  const filteredAndSortedDebugSteps = useMemo(() => {
    if (!debugSteps) return [];

    return [...debugSteps]
      .filter((step) => {
        const passesStatusFilter = statusFilters[step.status];
        const passesStrandFilter =
          !step.strand || strandFilters[step.strand] !== false;
        const passesTextFilter =
          !textFilter ||
          step.title?.toLowerCase().includes(textFilter.toLowerCase()) ||
          step.description?.toLowerCase().includes(textFilter.toLowerCase()) ||
          step.strand?.toLowerCase().includes(textFilter.toLowerCase());
        return passesStatusFilter && passesStrandFilter && passesTextFilter;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [debugSteps, statusFilters, strandFilters, textFilter]);

  if (debugLoading || nextStepsLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (debugError || nextStepsError) {
    return (
      <Typography color="error">
        Error loading data: {debugError?.message || nextStepsError?.message}
      </Typography>
    );
  }
  if (!nextStepsDebugData) {
    return <Typography>No debug data available</Typography>;
  }

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Next Steps Debug Page</Typography>
        <Button
          variant="contained"
          onClick={handleRefetch}
          disabled={updatingStatus || debugLoading || nextStepsLoading}
          startIcon={<Iconify icon="eva:refresh-outline" />}
          label="Refetch data"
        >
          Refetch
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" color="text.primary">
              {availableStepsCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Available Steps
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" color="text.primary">
              {userEventsCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              DynamoDB Events
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" color="info.main">
              {existingIncompleteStepsCount || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Existing Incomplete Steps
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" color="primary.main">
              {actualNextSteps?.length || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Next Steps
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="debug tabs"
        >
          <Tab label="Next Steps Analysis" id="debug-tab-0" />
          <Tab
            label={`User Events (${userEventsCount || 0})`}
            id="debug-tab-1"
          />
          <Tab
            label={`Active Steps (${actualNextSteps?.length || 0})`}
            id="debug-tab-2"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Typography variant="h5" gutterBottom>
          All Next Steps by Order ({debugSteps?.length || 0} total,{" "}
          {filteredAndSortedDebugSteps.length} shown)
        </Typography>

        {/* Filters */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
          }}
        >
          {/* Text Filter */}
          <Typography variant="subtitle1" gutterBottom>
            Filter by Text:
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by title, description, or strand..."
            value={textFilter}
            onChange={handleTextFilterChange}
            size="small"
            sx={{ mb: 3 }}
            label="Search climbs"
          />

          <Typography variant="subtitle1" gutterBottom>
            Filter by Status:
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.CURRENTLY_ACTIVE}
                  onChange={() => handleStatusFilterChange("CURRENTLY_ACTIVE")}
                  sx={{ color: getStatusColor("CURRENTLY_ACTIVE").color }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Currently Active
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.AVAILABLE_FIRST_IN_STRAND}
                  onChange={() =>
                    handleStatusFilterChange("AVAILABLE_FIRST_IN_STRAND")
                  }
                  sx={{
                    color: getStatusColor("AVAILABLE_FIRST_IN_STRAND").color,
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Available - First in Strand
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.AVAILABLE_NOT_FIRST_IN_STRAND}
                  onChange={() =>
                    handleStatusFilterChange("AVAILABLE_NOT_FIRST_IN_STRAND")
                  }
                  sx={{
                    color: getStatusColor("AVAILABLE_NOT_FIRST_IN_STRAND")
                      .color,
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Available - Not First in Strand
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.NOT_AVAILABLE}
                  onChange={() => handleStatusFilterChange("NOT_AVAILABLE")}
                  sx={{ color: getStatusColor("NOT_AVAILABLE").color }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Not Available
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.PREVIOUSLY_COMPLETED}
                  onChange={() =>
                    handleStatusFilterChange("PREVIOUSLY_COMPLETED")
                  }
                  sx={{ color: getStatusColor("PREVIOUSLY_COMPLETED").color }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Previously Completed
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.COMPLETED_AVAILABLE}
                  onChange={() =>
                    handleStatusFilterChange("COMPLETED_AVAILABLE")
                  }
                  sx={{ color: getStatusColor("COMPLETED_AVAILABLE").color }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Completed & Available
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={statusFilters.COMPLETED_NOT_AVAILABLE}
                  onChange={() =>
                    handleStatusFilterChange("COMPLETED_NOT_AVAILABLE")
                  }
                  sx={{
                    color: getStatusColor("COMPLETED_NOT_AVAILABLE").color,
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Completed & Not Available
                </Box>
              }
            />
          </FormGroup>

          {/* Strand Filter */}
          {availableStrands.length > 0 && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                Filter by Strand:
              </Typography>
              <FormGroup row>
                {availableStrands.map((strand) => (
                  <FormControlLabel
                    key={strand}
                    control={
                      <Checkbox
                        checked={strandFilters[strand] !== false}
                        onChange={() => handleStrandFilterChange(strand)}
                        sx={{ color: "primary.main" }}
                      />
                    }
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {strand}
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </>
          )}
        </Paper>

        <List>
          {filteredAndSortedDebugSteps.map((step) => (
            <DebugStepItem
              key={step.id}
              debugStep={step}
              showDetails={true}
              onEventGenerated={handleEventGenerated}
            />
          ))}
          {(!filteredAndSortedDebugSteps ||
            filteredAndSortedDebugSteps.length === 0) && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No debug steps found (try adjusting filters)
              </Typography>
            </Box>
          )}
        </List>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">
            User Events from DynamoDB ({userEvents?.length || 0})
          </Typography>
          {sortedAndPaginatedUserEvents.totalPages > 1 && (
            <Typography variant="body2" color="text.secondary">
              Page {userEventsPage} of {sortedAndPaginatedUserEvents.totalPages}
              (Showing {sortedAndPaginatedUserEvents.events.length} of{" "}
              {userEvents?.length || 0} events)
            </Typography>
          )}
        </Box>

        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell>Event Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAndPaginatedUserEvents.events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>
                    {new Date(
                      parseInt(event.timestamp) * 1000,
                    ).toLocaleString()}
                  </TableCell>
                  <TableCell>{event.eventType}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                    >
                      {Object.keys(JSON.parse(event.eventData)).map((key) => (
                        <Typography
                          key={key}
                          variant="body2"
                          color="text.secondary"
                        >
                          {key}: <b>{JSON.parse(event.eventData)[key]}</b>
                        </Typography>
                      ))}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {(!userEvents || userEvents.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary">
                      No user events found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* User Events Pagination */}
        {sortedAndPaginatedUserEvents.totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              count={sortedAndPaginatedUserEvents.totalPages}
              page={userEventsPage}
              onChange={(event, value) => setUserEventsPage(value)}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              label="User events pagination"
            />
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h5" gutterBottom>
          Active Next Steps (What Users See) - {actualNextSteps?.length || 0}{" "}
          steps
        </Typography>
        <Grid container spacing={3}>
          {actualNextSteps?.map((nextStep, index) => (
            <Grid item xs={12} sm={6} md={4} key={nextStep.id}>
              <NextStepCard
                nextStep={nextStep}
                showDismissButton={true}
                onDismiss={handleDismissStep}
                isTopThree={index < 3}
              />
            </Grid>
          ))}
          {(!actualNextSteps || actualNextSteps.length === 0) && (
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: "center",
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Typography color="text.secondary">
                  No active next steps found
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </TabPanel>
    </Box>
  );
});

export default NextStepsDebug;
